import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import imgMenu from '../../images/common/ic_hamburger.svg';
import closeButton from '../../images/common/cancel_black.svg';
import config from '../../config';
import { MENU, EXTERNAL_LINKS, PAGE_PATH } from '../../lib/constants';
import ColorSF2 from '../../styles/common/ColorSF2';
import {
  MenuButton,
  BackgroundBlock,
  MenuItem,
  MenuWrapper,
  MenuItemCloseButton,
  Version,
  CloseButton,
  CloseButtonImage,
  PolicyLinks,
  PolicyLink,
} from './Styles/SideMenuStyles';
import useScrollCheck from './hooks/useScrollCheck';
import { isCompanyAdmin, isServiceVendor } from '../../lib/tools';

const { MENU_ACCESS, SETTING_MENUS } = config;

const style = {
  link: {
    color: ColorSF2.white,
    textDecoration: 'none',
  },
};

/**
 * サイドメニューコンポーネント。
 * @param {Object} props - コンポーネントに渡されるプロパティ
 * @param {boolean} props.isMobile - 表示デバイスがモバイルかどうかを示すフラグ
 * @param {boolean} props.menuIsOpen - サイドメニューが開いているかどうかを示すフラグ
 * @param {function} props.onToggleMenu - メニューの開閉を処理するコールバック関数
 * @param {Object} props.version - クライアントとサーバーのバージョンの詳細を含むオブジェクト
 * @returns {ReactElement}
 */
const SideMenu = ({ isMobile, menuIsOpen, onToggleMenu, version }) => {
  const versionClient = `Client Version : ${version.client}`;
  const versionServer = `Server Version : ${version.server}`;
  const currentUserRole = useSelector(state => state.main.innerRole);
  const isCompanyAdminUser = isCompanyAdmin(currentUserRole);
  const isServiceVendorUser = isServiceVendor(currentUserRole);

  const isPermittedMenu = menu =>
    isCompanyAdminUser ||
    (MENU_ACCESS[currentUserRole] &&
      MENU_ACCESS[currentUserRole].indexOf(menu) !== -1);

  const showSettingMenu = SETTING_MENUS.some(
    menu =>
      MENU_ACCESS[currentUserRole] &&
      MENU_ACCESS[currentUserRole].some(access => access === menu),
  );

  const { menuWrapperRef, isOverflowing } = useScrollCheck(menuIsOpen);

  return (
    <div>
      <MenuButton
        className="button"
        id="openMenuButton"
        role="presentation"
        onClick={() => onToggleMenu()}
      >
        <img src={imgMenu} alt="" />
      </MenuButton>
      <div>
        <BackgroundBlock
          id="menuClose"
          open={menuIsOpen}
          onClick={() => onToggleMenu()}
        />
        <MenuWrapper id="menuWrapper" open={menuIsOpen} ref={menuWrapperRef}>
          <MenuItemCloseButton
            id="menuCloseButton"
            onClick={() => onToggleMenu()}
          >
            <CloseButton className="button">
              <CloseButtonImage src={closeButton} alt="closeButton" />
            </CloseButton>
          </MenuItemCloseButton>
          {!isServiceVendorUser && (
            <MenuItem id="menuSummary" default>
              <FormattedMessage id="mainTitleBar.menu.summary" />
            </MenuItem>
          )}
          {isPermittedMenu(MENU.dashBoard) && (
            <Link to="/ds/" style={style.link}>
              <MenuItem indent className="menuItem" id="menuDashboard">
                <FormattedMessage id="mainTitleBar.menu.dashboard" />
              </MenuItem>
            </Link>
          )}
          {isPermittedMenu(MENU.media) && (
            <Link to="/ds/timeLine" style={style.link}>
              <MenuItem indent className="menuItem" id="menuMedia">
                <FormattedMessage id="mainTitleBar.menu.media" />
              </MenuItem>
            </Link>
          )}
          {isPermittedMenu(MENU.report) && (
            <Link to="/ds/report" style={style.link}>
              <MenuItem indent className="menuItem" id="menuReport">
                <FormattedMessage id="mainTitleBar.menu.report" />
              </MenuItem>
            </Link>
          )}
          {isPermittedMenu(MENU.issue) && !isMobile && (
            <Link to="/ds/issue" style={style.link}>
              <MenuItem indent className="menuItem" id="menuIssue">
                <FormattedMessage id="mainTitleBar.menu.issue" />
              </MenuItem>
            </Link>
          )}
          {showSettingMenu && (
            <Fragment key="SettingsMenu">
              <MenuItem id="menuSetting" default>
                <FormattedMessage id="mainTitleBar.menu.settings" />
              </MenuItem>
              {isPermittedMenu(MENU.company) && !isMobile && (
                <Link to="/ds/company" style={style.link}>
                  <MenuItem indent className="menuItem" id="menuCompany">
                    <FormattedMessage id="mainTitleBar.menu.company" />
                  </MenuItem>
                </Link>
              )}
              {isPermittedMenu(MENU.group) && !isMobile && (
                <Link to="/ds/group" style={style.link}>
                  <MenuItem indent className="menuItem" id="menuGroup">
                    <FormattedMessage id="mainTitleBar.menu.group" />
                  </MenuItem>
                </Link>
              )}
              {isPermittedMenu(MENU.staff) && !isMobile && (
                <Link to="/ds/staff" style={style.link}>
                  <MenuItem indent className="menuItem" id="menuStaff">
                    <FormattedMessage id="mainTitleBar.menu.staff" />
                  </MenuItem>
                </Link>
              )}
              {isPermittedMenu(MENU.device) && !isMobile && (
                <Link to="/ds/device" style={style.link}>
                  <MenuItem indent className="menuItem" id="menuDevice">
                    <FormattedMessage id="mainTitleBar.menu.device" />
                  </MenuItem>
                </Link>
              )}
              {isPermittedMenu(MENU.master) && !isMobile && (
                <Link to="/ds/master" style={style.link}>
                  <MenuItem
                    indent
                    className="menuItem"
                    id="menuCallOperator"
                    onClick={() => onToggleMenu()}
                  >
                    <FormattedMessage id="mainTitleBar.menu.master" />
                  </MenuItem>
                </Link>
              )}
              {isPermittedMenu(MENU.actionLog) && !isMobile && (
                <Link to="/ds/action-log" style={style.link}>
                  <MenuItem indent className="menuItem" id="menuActionLog">
                    <FormattedMessage id="mainTitleBar.menu.actionLog" />
                  </MenuItem>
                </Link>
              )}
              {isPermittedMenu(MENU.accessControl) && !isMobile && (
                <Link to={PAGE_PATH.DS_ACCESS_CONTROL} style={style.link}>
                  <MenuItem indent className="menuItem" id="menuAccessControl">
                    <FormattedMessage id="mainTitleBar.menu.accessControl" />
                  </MenuItem>
                </Link>
              )}
            </Fragment>
          )}
          <PolicyLinks overflow={isOverflowing}>
            <PolicyLink
              href={EXTERNAL_LINKS.TERMS_OF_SERVICE}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id="mainTitleBar.menu.termsOfServices" />
            </PolicyLink>
            <PolicyLink
              href={EXTERNAL_LINKS.PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id="mainTitleBar.menu.privacyPolicy" />
            </PolicyLink>
          </PolicyLinks>
          <Version overflow={isOverflowing}>
            {versionClient}
            <br />
            {versionServer}
          </Version>
        </MenuWrapper>
      </div>
    </div>
  );
};

SideMenu.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  menuIsOpen: PropTypes.bool.isRequired,
  onToggleMenu: PropTypes.func.isRequired,
  version: PropTypes.object.isRequired,
};

export default SideMenu;
